import { ILead } from './lead.dto';
import {
  IsNotEmpty,
  IsString,
  IsOptional,
  IsNumber,
  IsBoolean
} from 'class-validator';
import { IBaseResponse } from './base-response.dto';
import { ISchedule } from './schedule.dto';
import { IForm, IFormField } from './form.dto';

export class IService {
  organization: string;
  _id: string;
  label: string;
  status: ServiceStatus;
  description?: string;
  instructions?: string;
  /** time in minutes */
  slotDuration: number;
  /** time in minutes */
  slotGap: number;
  schedules: ISchedule[] | string[];
  /** time in hours */
  minLeadTime?: number;
  /** time in hours */
  maxLeadTime?: number;
  createdAt?: Date;
  updatedAt?: Date;
  form?: string | IForm;
  isHidden?: boolean;
  // dynamic data
  extras?: any;
  /** @deprecated use form instead */
  fields?: IFormField[];
  payment: IServicePayment;
  isVirtualMeeting: boolean;
  extraTimePerDependent: number;
  extraPricePerDependent: number;
  /** ex. city, province, ... */
  location?: string;
  assignmentStrategy: ServiceAssignmentStrategy;
  /** @deprecated use assignmentStrategy === queue */
  isQueue?: boolean;
}

export class IServicePayment {
  enabled: boolean;
  payLater: boolean;
  /** wont create a charge, instead create a customer in stripe to charge him later
   * https://stripe.com/docs/saving-cards
   */
  saveCardOnly: boolean;
  price: number;
  currency: 'USD' | 'CAD';
  /** min 0, max 100, percent of service coverage, 100 = free, 0 = full price */
  hcCoverage: number;
  qtyField: string;
}

export type ServicePaymentType = 'full_cover' | 'not_covered' | 'partial_cover';

export type ServiceStatus = 'active' | 'disabled';

export class IBookingsMap {
  [key: string]: IAvailabilitySchedule; // {'2021-02-12': [{100: 2, 200: 1}]}
}
export class IAvailabilitySchedule {
  [key: string]: IAvailabilitySlot; // {'2021-02-12': {100: 2, 200: 1}}
}
export class IAvailabilitySlot {
  [type: number]: number; // {100: 2} where 123 is the startMinute and 2 is number of available slots
}

export class IAvailabilityQueue {
  queue: {
    label: string;
  };
}

export type IServiceAvailabilityV2 = Record<
  string,
  Record<string, [number, number][]>
>;

export class IAppointmentconsent {
  consentHTML: string;
  stripePublicKey: string;
  apptInstruction: string;
}
/* -------------------------------------------------------------------------- */
/*                               Create new Service                              */
/* -------------------------------------------------------------------------- */

export class CreateServiceDto {
  @IsNotEmpty()
  @IsString()
  status: ServiceStatus;

  @IsNotEmpty()
  @IsString()
  label: string;

  @IsOptional()
  @IsString()
  description: string;

  @IsOptional()
  @IsString()
  instructions: string;
}

export class CreateServiceResponse extends IBaseResponse {
  service: IService;
}

/* -------------------------------------------------------------------------- */
/*                                   Update Service                              */
/* -------------------------------------------------------------------------- */

export class UpdateServiceDto {
  @IsString()
  @IsOptional()
  type?: ServiceStatus;

  @IsString()
  @IsOptional()
  owner?: string;

  @IsString()
  @IsOptional()
  relatedToLead?: string | ILead;

  @IsString()
  @IsOptional()
  form?: string;

  @IsString()
  @IsOptional()
  status?: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsString()
  @IsOptional()
  notes?: string;

  @IsNumber()
  @IsOptional()
  slotDuration?: number;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class UpdateServiceResponse extends IBaseResponse {
  id: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Get Services                                 */
/* -------------------------------------------------------------------------- */
export class GetServicesDto {
  _id?: string;
  label?: string;
  description?: string;
  status?: string | Object;
  form?: string;
  schedules?: string;
  isHidden?: boolean | Object;
  location?: string;
}

export class ListServicesPublicDto {
  location?: string;
  serviceId?: string;
  label?: string;
}

export type GetServicesResponse = IService[];

export type ServiceAssignmentStrategy = 'none' | 'round-robin' | 'queue';
